import { SolButton, SolIcon, SolTextField } from "@solstice/sol-react";
import { useFormik } from "formik";
import { useApiCall } from "hooks";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { patchProfileAPI } from "services";
import { BrokerProps, ProfileProps } from "types";
import { LoadingPage } from "ui-molecules";
import * as Yup from "yup";
import { getApiPayloadFromForm } from "utils";

interface MarketProps {
  profile: ProfileProps | null;
  setProfile?: any;
  isLoading?: boolean;
  broker: BrokerProps | null;
  setBroker: any;
}

const INITIAL_VALUES = {
  job_title: "",
};

const validationSchema = Yup.object().shape({
  job_title: Yup.string().required("This field is required"),
});

const Market: React.FC<MarketProps> = ({
  profile,
  setProfile,
  isLoading,
  setBroker,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [patchProfile] = useApiCall(patchProfileAPI);

  const {
    handleBlur,
    setFieldValue,
    values,
    setValues,
    touched,
    errors,
    validateForm,
    validateField,
    setFieldTouched,
  } = useFormik({
    initialValues: INITIAL_VALUES,
    validationSchema: validationSchema,
    onSubmit: async () => {},
  });

  useEffect(() => {
    if (!profile || profile?.id?.toString() !== searchParams?.get("tab"))
      return;
    let formValues: any = {};
    Object.keys(INITIAL_VALUES)?.forEach((key: string) => {
      if (key === "job_title") {
        formValues = {
          ...formValues,
          [key]: profile?.job_title,
        };
        return;
      }
      formValues = {
        ...formValues,
        [key]: ((profile as any)?.[key] || [])?.map((item: any) => ({
          value: item?.id,
          label: item?.name,
        })),
      };
    });
    setValues({
      ...formValues,
    });
  }, [profile]);

  const handleUpdate = (key: "job_title", values: any) => {
    try {
      setFieldValue(key, values);

      let payload = getApiPayloadFromForm(profile, {
        [key]: values,
      });
      if (!payload || !Object.keys(payload)?.length) return;
      patchProfile({
        pk: profile?.id,
        ...payload,
      }).then((res: ProfileProps) => {
        if (!res) return;
        setProfile({
          ...profile,
          [key]: res?.[key],
        });
        setBroker((prev: BrokerProps) => {
          let updatedProfiles = prev?.profiles || [];
          const index = (updatedProfiles || []).findIndex(
            (item) => item?.id?.toString() === res?.id?.toString()
          );
          if (index > -1) {
            updatedProfiles[index] = {
              ...updatedProfiles?.[index],
              [key]: res?.[key],
            };
          }
          return {
            ...prev,
            profiles: updatedProfiles,
          };
        });
      });
    } catch (err) {}
  };

  const handleNext = () => {
    validateForm().then((errors) => {
      const isFormValid = Object.keys(errors).length === 0;
      if (isFormValid) {
        searchParams.set("sub", "awards");
        setSearchParams(searchParams);
      } else {
        for (const key in errors) {
          setFieldTouched(key, true);
        }
      }
    });
  };

  const updateField = (key: "job_title", value: any) => {
    validateField(key).then(() => {
      if (!errors[key]) {
        handleUpdate(key, value);
      } else {
        setFieldTouched(key, true);
      }
    });
  };

  return (
    <>
      {isLoading ? (
        <LoadingPage />
      ) : (
        <div className="w-full px-10 pt-8">
          <div>
            <p className="text-xl mb-1">
              Job Title <span className="text-jll-stroke-danger ml-1">*</span>
            </p>
            <p className="text-sm text-jll-text-base-subdued mb-2">
              Enter your official JLL role title as it appears in company
              records.
            </p>
          </div>
          <div className="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-10 mb-14">
            <div className="col-span-1">
              <SolTextField
                value={values.job_title}
                onSol-input={(e: any) => setFieldValue("job_title", e?.detail)}
                id="job_title"
                onBlur={(e) => {
                  handleBlur(e);
                  updateField("job_title", values.job_title);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    updateField("job_title", values.job_title);
                  }
                }}
                size="small"
                errorLabel={touched?.job_title ? errors?.job_title : ""}
              />
            </div>
          </div>

          <div className="flex flex-row items-center space-x-10">
            <SolButton
              variant="outlined"
              onSol-click={() => {
                searchParams.set("sub", "geography");
                setSearchParams(searchParams);
              }}
            >
              <SolIcon icon="arrow_back" className="!w-5 !h-5" />
              Back
            </SolButton>
            <SolButton variant="outlined" onSol-click={handleNext}>
              Next
              <SolIcon icon="arrow_right_alt" />
            </SolButton>
          </div>
        </div>
      )}
    </>
  );
};

export default Market;
